import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import useOperationalStore, { PIPELINE } from '../platform/store/useOperationalStore';
import { tsToMs, getCycleMs } from '../platform/history/serviceRecord';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_TO_STAGE_ID = {};
PIPELINE.forEach(stage => stage.statuses.forEach(s => { STATUS_TO_STAGE_ID[s] = stage.id; }));

function computeStageTimes(truck) {
  const history = [...(truck.statusHistory || [])]
    .map(h => ({ ...h, ms: tsToMs(h.timeEntered) || 0 }))
    .filter(h => h.ms > 0)
    .sort((a, b) => a.ms - b.ms);

  const stageTimes = {};
  for (let i = 0; i < history.length - 1; i++) {
    const dur = history[i + 1].ms - history[i].ms;
    if (dur <= 0) continue;
    const stageId = STATUS_TO_STAGE_ID[history[i].status];
    if (stageId) stageTimes[stageId] = (stageTimes[stageId] || 0) + dur;
  }
  return stageTimes;
}

function fmtDays(ms) {
  if (!ms || ms <= 0) return '—';
  const days = ms / 86_400_000;
  if (days < 0.042) return '<1h';         // under 1 hour
  if (days < 1) return `${Math.round(days * 24)}h`;
  return `${days.toFixed(1)}d`;
}

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(tsToMs(ts)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const MS_PER_DAY = 86_400_000;

const RANGE_OPTIONS = [
  { label: '30 days', days: 30 },
  { label: '60 days', days: 60 },
  { label: '90 days', days: 90 },
  { label: 'All time', days: null },
];

// ─── Main component ───────────────────────────────────────────────────────────

export default function CycleTimeReport() {
  const navigate = useNavigate();
  const trucks = useOperationalStore(s => s.trucks);
  const [rangeDays, setRangeDays] = useState(30);

  const { currentJobs, priorJobs, stageRows, maxStageMs } = useMemo(() => {
    const now = Date.now();
    const rangeMs   = rangeDays ? rangeDays * MS_PER_DAY : null;
    const cutoff    = rangeMs ? now - rangeMs : null;
    const priorFrom = rangeMs ? now - 2 * rangeMs : null;

    function filterCompleted(from, to) {
      return trucks.filter(t => {
        if (t.status !== 'completed') return false;
        const closedMs = tsToMs(t.completionTime || t.lastStatusChange);
        if (!closedMs) return false;
        if (from && closedMs < from) return false;
        if (to   && closedMs >= to)  return false;
        return true;
      });
    }

    const cJobs = filterCompleted(cutoff, null);
    const pJobs = rangeMs ? filterCompleted(priorFrom, cutoff) : [];

    // Per-stage averages for current period
    const stageSums   = {};
    const stageCounts = {};
    cJobs.forEach(truck => {
      const times = computeStageTimes(truck);
      Object.entries(times).forEach(([id, ms]) => {
        stageSums[id]   = (stageSums[id]   || 0) + ms;
        stageCounts[id] = (stageCounts[id] || 0) + 1;
      });
    });

    const rows = PIPELINE.map(stage => ({
      id: stage.id,
      label: stage.label,
      avgMs: stageCounts[stage.id] ? stageSums[stage.id] / stageCounts[stage.id] : 0,
      count: stageCounts[stage.id] || 0,
    }));

    const maxMs = Math.max(...rows.map(r => r.avgMs), 1);

    return { currentJobs: cJobs, priorJobs: pJobs, stageRows: rows, maxStageMs: maxMs };
  }, [trucks, rangeDays]);

  // ── Headline numbers ───────────────────────────────────────────────────────
  const currentCycleMsArr = currentJobs.map(getCycleMs).filter(Boolean);
  const priorCycleMsArr   = priorJobs.map(getCycleMs).filter(Boolean);
  const currentAvgMs = currentCycleMsArr.length ? currentCycleMsArr.reduce((a, b) => a + b, 0) / currentCycleMsArr.length : null;
  const priorAvgMs   = priorCycleMsArr.length   ? priorCycleMsArr.reduce((a, b)   => a + b, 0) / priorCycleMsArr.length   : null;
  const currentDays  = currentAvgMs ? currentAvgMs / MS_PER_DAY : null;
  const priorDays    = priorAvgMs   ? priorAvgMs   / MS_PER_DAY : null;
  const delta        = currentDays != null && priorDays != null ? currentDays - priorDays : null;

  // ── Export helpers ─────────────────────────────────────────────────────────
  function exportCSV() {
    const lines = [
      ['RO#', 'Company', 'Truck', 'Intake', 'Completed', 'Days in Shop',
        ...PIPELINE.map(s => `${s.label} (days)`)
      ].join(','),
      ...currentJobs.map(t => {
        const cycleMs = getCycleMs(t);
        const cycleDays = cycleMs ? (cycleMs / MS_PER_DAY).toFixed(2) : '';
        const stageTimes = computeStageTimes(t);
        const stageCols = PIPELINE.map(s => {
          const ms = stageTimes[s.id];
          return ms ? (ms / MS_PER_DAY).toFixed(2) : '';
        });
        return [
          `"${t.repairOrder || t.id}"`,
          `"${t.companyName || ''}"`,
          `"${[t.year, t.make, t.model].filter(Boolean).join(' ')}"`,
          fmtDate(t.startTime),
          fmtDate(t.completionTime || t.lastStatusChange),
          cycleDays,
          ...stageCols,
        ].join(',');
      }),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cycle-time-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportPDF() {
    const doc = new jsPDF();
    const rangeLabel = RANGE_OPTIONS.find(r => r.days === rangeDays)?.label ?? 'All time';
    let y = 20;

    doc.setFontSize(18);
    doc.text('REKON — Cycle Time Report', 15, y);
    y += 8;
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`${rangeLabel}  ·  Generated ${new Date().toLocaleDateString()}  ·  ${currentJobs.length} completed jobs`, 15, y);
    y += 12;

    doc.setFontSize(14);
    doc.setTextColor(0);
    const headline = currentDays != null
      ? `Avg days in shop: ${currentDays.toFixed(1)}d`
      : 'No completed jobs in range';
    doc.text(headline, 15, y);
    if (delta != null) {
      y += 6;
      doc.setFontSize(11);
      doc.setTextColor(delta < 0 ? 34 : 200, delta < 0 ? 197 : 0, 0);
      doc.text(`${delta < 0 ? '▼' : '▲'} ${Math.abs(delta).toFixed(1)}d vs prior ${rangeLabel}`, 15, y);
      doc.setTextColor(0);
    }
    y += 12;

    doc.setFontSize(12);
    doc.text('Avg time per stage (current period):', 15, y);
    y += 7;
    doc.setFontSize(10);
    stageRows.forEach(row => {
      if (!row.avgMs) return;
      doc.text(`  ${row.label.padEnd(12)} ${fmtDays(row.avgMs)}  (${row.count} jobs)`, 15, y);
      y += 6;
    });

    y += 6;
    doc.setFontSize(12);
    doc.text('Completed jobs:', 15, y);
    y += 7;
    doc.setFontSize(9);
    currentJobs.slice(0, 40).forEach(t => {
      const cyc = getCycleMs(t);
      const line = `${t.repairOrder || t.id}  ${(t.companyName || '').slice(0, 25).padEnd(25)}  ${fmtDays(cyc)}  Completed ${fmtDate(t.completionTime || t.lastStatusChange)}`;
      doc.text(line, 15, y);
      y += 5;
      if (y > 275) { doc.addPage(); y = 15; }
    });

    doc.save(`cycle-time-report-${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  const empty = currentJobs.length === 0;

  return (
    <div style={S.page}>

      {/* Header */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          <button style={S.backBtn} onClick={() => navigate('/analytics')}>← Analytics</button>
          <div>
            <div style={S.title}>Cycle Time Report</div>
            <div style={S.sub}>{currentJobs.length} completed jobs · {RANGE_OPTIONS.find(r => r.days === rangeDays)?.label}</div>
          </div>
        </div>
        <div style={S.headerRight}>
          <div style={S.rangePicker}>
            {RANGE_OPTIONS.map(opt => (
              <button
                key={opt.label}
                style={{ ...S.rangeBtn, ...(rangeDays === opt.days ? S.rangeBtnActive : {}) }}
                onClick={() => setRangeDays(opt.days)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button style={S.exportBtn} onClick={exportCSV}>↓ CSV</button>
          <button style={S.exportBtn} onClick={exportPDF}>↓ PDF</button>
        </div>
      </div>

      <div style={S.body}>

        {/* Headline KPI */}
        <div style={S.kpiRow}>
          <div style={S.kpiCard}>
            <div style={S.kpiLabel}>Avg days in shop</div>
            <div style={S.kpiVal}>
              {currentDays != null ? `${currentDays.toFixed(1)}d` : '—'}
            </div>
            {delta != null && (
              <div style={{ ...S.kpiDelta, color: delta < 0 ? '#22c55e' : '#ef4444' }}>
                {delta < 0 ? '▼' : '▲'} {Math.abs(delta).toFixed(1)}d vs prior period ({priorDays?.toFixed(1)}d)
              </div>
            )}
            {delta == null && priorDays == null && currentDays != null && (
              <div style={S.kpiDeltaMuted}>No prior period data</div>
            )}
          </div>
          <div style={S.kpiCard}>
            <div style={S.kpiLabel}>Jobs completed</div>
            <div style={S.kpiVal}>{currentJobs.length}</div>
            {priorJobs.length > 0 && (
              <div style={{ ...S.kpiDelta, color: currentJobs.length >= priorJobs.length ? '#22c55e' : '#ef4444' }}>
                {currentJobs.length >= priorJobs.length ? '▲' : '▼'} vs {priorJobs.length} prior period
              </div>
            )}
          </div>
          <div style={S.kpiCard}>
            <div style={S.kpiLabel}>Fastest job</div>
            <div style={S.kpiVal}>
              {currentCycleMsArr.length ? fmtDays(Math.min(...currentCycleMsArr)) : '—'}
            </div>
          </div>
          <div style={S.kpiCard}>
            <div style={S.kpiLabel}>Slowest job</div>
            <div style={S.kpiVal}>
              {currentCycleMsArr.length ? fmtDays(Math.max(...currentCycleMsArr)) : '—'}
            </div>
          </div>
        </div>

        {empty ? (
          <div style={S.emptyState}>No completed jobs in selected range.</div>
        ) : (
          <>
            {/* Stage breakdown */}
            <div style={S.section}>
              <div style={S.sectionTitle}>Avg time per stage</div>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Stage</th>
                    <th style={S.th}>Avg time</th>
                    <th style={S.th}>Jobs with data</th>
                    <th style={{ ...S.th, width: '40%' }}>Relative</th>
                  </tr>
                </thead>
                <tbody>
                  {stageRows.map(row => (
                    <tr key={row.id}>
                      <td style={S.td}>{row.label}</td>
                      <td style={{ ...S.td, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {row.avgMs ? fmtDays(row.avgMs) : '—'}
                      </td>
                      <td style={{ ...S.td, color: '#64748b' }}>{row.count || '—'}</td>
                      <td style={S.td}>
                        {row.avgMs > 0 && (
                          <div style={S.barTrack}>
                            <div style={{
                              ...S.barFill,
                              width: `${Math.round((row.avgMs / maxStageMs) * 100)}%`,
                              background: row.avgMs / maxStageMs > 0.6 ? '#f59e0b' : '#0B2265',
                            }} />
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Job list */}
            <div style={S.section}>
              <div style={S.sectionTitle}>Completed jobs</div>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>RO #</th>
                    <th style={S.th}>Company</th>
                    <th style={S.th}>Vehicle</th>
                    <th style={S.th}>Intake</th>
                    <th style={S.th}>Completed</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>Days in shop</th>
                  </tr>
                </thead>
                <tbody>
                  {currentJobs
                    .slice()
                    .sort((a, b) => (tsToMs(b.completionTime || b.lastStatusChange) || 0) - (tsToMs(a.completionTime || a.lastStatusChange) || 0))
                    .map(t => {
                      const cycleMs = getCycleMs(t);
                      const days = cycleMs ? cycleMs / MS_PER_DAY : null;
                      const slowFlag = days && currentDays && days > currentDays * 1.5;
                      return (
                        <tr key={t.id}>
                          <td style={S.td}>{t.repairOrder || t.id.slice(0, 8)}</td>
                          <td style={S.td}>{t.companyName || '—'}</td>
                          <td style={{ ...S.td, color: '#64748b', fontSize: 12 }}>
                            {[t.year, t.make, t.model].filter(Boolean).join(' ') || '—'}
                          </td>
                          <td style={{ ...S.td, color: '#64748b', fontSize: 12 }}>{fmtDate(t.startTime)}</td>
                          <td style={{ ...S.td, color: '#64748b', fontSize: 12 }}>{fmtDate(t.completionTime || t.lastStatusChange)}</td>
                          <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: slowFlag ? '#ef4444' : '#0f172a' }}>
                            {days ? `${days.toFixed(1)}d` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  page: {
    minHeight: '100vh',
    background: '#f1f5f9',
    fontFamily: "'Inter','Segoe UI',sans-serif",
    color: '#0f172a',
  },
  header: {
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
    padding: '14px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 16 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 8 },
  backBtn: {
    background: 'none',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    padding: '7px 14px',
    cursor: 'pointer',
    fontSize: 13,
    color: '#64748b',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  title: { fontSize: 18, fontWeight: 800, letterSpacing: '-0.3px', color: '#0f172a' },
  sub:   { fontSize: 12, color: '#94a3b8', marginTop: 2 },
  rangePicker: { display: 'flex', gap: 4 },
  rangeBtn: {
    padding: '6px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    background: '#fff',
    cursor: 'pointer',
    fontSize: 13,
    color: '#64748b',
    fontWeight: 600,
  },
  rangeBtnActive: {
    background: '#0B2265',
    color: '#fff',
    borderColor: '#0B2265',
  },
  exportBtn: {
    padding: '7px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    background: '#fff',
    cursor: 'pointer',
    fontSize: 13,
    color: '#334155',
    fontWeight: 700,
  },
  body: { padding: '24px 24px 48px' },
  kpiRow: { display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' },
  kpiCard: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '18px 24px',
    minWidth: 160,
    flex: 1,
  },
  kpiLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 6 },
  kpiVal:   { fontSize: 36, fontWeight: 900, letterSpacing: '-1px', color: '#0f172a', lineHeight: 1 },
  kpiDelta: { fontSize: 12, fontWeight: 700, marginTop: 6 },
  kpiDeltaMuted: { fontSize: 12, color: '#94a3b8', marginTop: 6 },
  emptyState: {
    textAlign: 'center',
    padding: '80px 24px',
    color: '#94a3b8',
    fontSize: 16,
    fontWeight: 600,
    background: '#fff',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
  },
  section: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '20px 24px',
    marginBottom: 20,
    overflowX: 'auto',
  },
  sectionTitle: { fontSize: 14, fontWeight: 800, color: '#0f172a', marginBottom: 14, letterSpacing: '-0.2px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: {
    textAlign: 'left',
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: '#94a3b8',
    paddingBottom: 8,
    borderBottom: '1px solid #f1f5f9',
    paddingRight: 16,
  },
  td: {
    padding: '10px 16px 10px 0',
    borderBottom: '1px solid #f8fafc',
    verticalAlign: 'middle',
  },
  barTrack: {
    height: 8,
    background: '#f1f5f9',
    borderRadius: 4,
    overflow: 'hidden',
    width: '100%',
  },
  barFill: {
    height: '100%',
    borderRadius: 4,
    transition: 'width 0.3s ease',
  },
};
